import { apiBlob, apiEventStream, apiRequest, type ApiStreamEvent } from './http';
import type {
  AiResourcePolicy, AiResourceUsageReport, SaveAiResourcePolicyInput,
} from './aiResources';

export type AssistantContentType = 'Text' | 'HtmlReport' | 'TransactionProposal' | 'ReceiptProposal'
  | 'InvoiceProposal' | 'SupplierPaymentProposal' | 'BankTransferProposal' | 'CashTransactionProposal'
  | 'StatementImportProposal' | 'PersonProposal';
export interface AssistantConfiguration {
  provider: string; model: string; isConfigured: boolean;
  speechToTextEnabled: boolean; textToSpeechEnabled: boolean;
  canGenerateReports: boolean; canProposeTransactions: boolean;
  canConfirmTransactions: boolean; canProposeReceipts: boolean; canConfirmReceipts: boolean;
  canProposeInvoices: boolean; canConfirmInvoices: boolean;
  canProposeSupplierPayments: boolean; canConfirmSupplierPayments: boolean;
  canProposeBankTransfers: boolean; canConfirmBankTransfers: boolean;
  canProposeCashTransactions: boolean; canConfirmCashTransactions: boolean;
  canProposeStatementImports: boolean; canConfirmStatementImports: boolean;
  canManageSchedules: boolean;
  canInspectEvidence: boolean; canManageMemory: boolean; canManageKnowledge: boolean;
  canReadAdvisories: boolean; canManageAdvisories: boolean; supervisionMode: string;
  isEnabled: boolean; scheduledExecutionEnabled: boolean; audioProvider: string;
  sensitiveDocumentAutomationEnabled: boolean;
  canProposePersons: boolean; canConfirmPersons: boolean;
  canReadResourceUsage: boolean; canManageResourcePolicies: boolean;
}

export function recordedAudioFileName(contentType: string): string {
  const normalized = contentType.toLowerCase();
  if (normalized.includes('mp4')) return 'voice.m4a';
  if (normalized.includes('ogg')) return 'voice.ogg';
  if (normalized.includes('wav')) return 'voice.wav';
  if (normalized.includes('mpeg') || normalized.includes('mp3')) return 'voice.mp3';
  return 'voice.webm';
}
export interface AssistantConversation {
  id: string; title: string; createdAt: string; updatedAt: string; version: number;
}
export interface AssistantMessage {
  id: string; conversationId: string; role: 'User' | 'Assistant';
  contentType: AssistantContentType; content: string; createdAt: string;
  journalId: string | null; proposalId: string | null; receiptId: string | null;
  model: string; traceId: string | null;
}
export interface AssistantAudioReply {
  content: string; contentType: string; fileName: string;
}
export interface AssistantVoiceTurn {
  transcript: string; transcriptMessage: AssistantMessage; message: AssistantMessage;
  audio: AssistantAudioReply | null; audioErrorCode: string | null;
}
export interface AssistantSchedule {
  id: string; name: string; prompt: string; intervalMinutes: number;
  nextRunAt: string; lastRunAt: string | null; lastError: string | null;
  isEnabled: boolean; version: number;
}
export interface AssistantEvidence {
  evidenceId: string; evidenceType: string; sourceTitle: string; citationLabel: string;
  articleReference: string; page: number | null; effectiveFrom: string | null;
  effectiveTo: string | null; language: string; text: string; score: number; metadataJson: string;
}
export interface AssistantToolCall {
  id: string; toolName: string; toolVersion: string; argumentsJson: string; resultJson: string;
  status: string; errorCode: string; durationMilliseconds: number; createdAt: string;
}
export interface AssistantActionProposal {
  id: string; traceId: string; actionType: string; payloadJson: string; explanation: string;
  expectedEffectJson: string; riskLevel: string; status: string; journalId: string | null;
  receiptId: string | null; invoiceId: string | null; supplierPaymentId: string | null;
  bankTransferId: string | null; cashTransactionId: string | null; statementImportId: string | null;
  verificationJson: string; expiresAt: string; createdAt: string; completedAt: string | null;
}
export interface AssistantTrace {
  id: string; conversationId: string; correlationId: string; language: string; intent: string;
  agentCode: string; agentVersion: string; promptCode: string; promptVersion: string; model: string;
  modelInfoJson: string; request: string; planJson: string; decisionSummaryJson: string;
  policyDecisionJson: string; riskLevel: string; status: string; finalResponse: string;
  errorCode: string; errorMessage: string; inputTokens: number; outputTokens: number;
  estimatedCost: number; modelCalls: number; toolCalls: number; durationMilliseconds: number;
  startedAt: string; completedAt: string | null; evidence: readonly AssistantEvidence[];
  toolCallsDetail: readonly AssistantToolCall[]; action: AssistantActionProposal | null;
}
export interface AssistantMemory {
  id: string; userId: string | null; scope: 'User' | 'Organization'; memoryType: string;
  statement: string; payloadJson: string; sourceType: string; sourceId: string; sensitivity: string;
  confidence: number; status: string; approvedBy: string | null; validFrom: string | null;
  validTo: string | null; expiresAt: string | null; createdAt: string; lastAccessedAt: string;
}
export interface AssistantAdvisory {
  id: string; advisoryType: string; severity: 'Info' | 'Warning' | 'Critical';
  title: string; message: string; payloadJson: string;
  status: 'Active' | 'Dismissed' | 'Resolved'; detectedAt: string; updatedAt: string;
  dismissedBy: string | null; dismissedAt: string | null;
}
export interface AssistantUsage {
  tokensToday: number; costThisMonth: number; dailyTokenBudget: number; monthlyCostBudget: number;
  traceRetentionDays: number; supervisionMode: string; maximumAutonomyLevel: number;
}
export interface AssistantKnowledgeDocument {
  id: string; title: string; language: string; sourceHash: string;
  status: 'Draft' | 'Review' | 'Published' | 'Archived' | 'Failed';
  characterCount: number; chunkCount: number; createdAt: string;
}
export interface AssistantKnowledgeChunk {
  id: string; documentId: string; chunkIndex: number; headingPath: string;
  citationLabel: string; rawText: string; metadataJson: string; createdAt: string;
}

export const assistantApi = {
  configuration: () => apiRequest<AssistantConfiguration>('/assistant/configuration'),
  conversations: () => apiRequest<readonly AssistantConversation[]>('/assistant/conversations'),
  createConversation: (title?: string) => apiRequest<AssistantConversation>('/assistant/conversations', {
    method: 'POST', body: JSON.stringify({ title: title ?? null }),
  }),
  messages: (id: string) => apiRequest<readonly AssistantMessage[]>(`/assistant/conversations/${id}/messages`),
  traces: (conversationId?: string) => apiRequest<readonly AssistantTrace[]>(
    `/assistant/traces${conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : ''}`),
  trace: (traceId: string) => apiRequest<AssistantTrace>(`/assistant/traces/${traceId}`),
  usage: () => apiRequest<AssistantUsage>('/assistant/usage'),
  resourceReport: () => apiRequest<AiResourceUsageReport>('/assistant/resources/report'),
  resourcePolicies: () => apiRequest<readonly AiResourcePolicy[]>('/assistant/resources/policies'),
  createResourcePolicy: (input: SaveAiResourcePolicyInput) => apiRequest<AiResourcePolicy>(
    '/assistant/resources/policies', { method: 'POST', body: JSON.stringify(input) }),
  updateResourcePolicy: (policyId: string, input: SaveAiResourcePolicyInput) =>
    apiRequest<AiResourcePolicy>(`/assistant/resources/policies/${encodeURIComponent(policyId)}`, {
      method: 'PUT', body: JSON.stringify(input),
    }),
  advisories: (includeDismissed = false) => apiRequest<readonly AssistantAdvisory[]>(
    `/assistant/advisories?includeDismissed=${includeDismissed}`),
  dismissAdvisory: (advisoryId: string) => apiRequest<void>(
    `/assistant/advisories/${advisoryId}/dismiss`, { method: 'POST' }),
  memories: () => apiRequest<readonly AssistantMemory[]>('/assistant/memories'),
  knowledge: () => apiRequest<readonly AssistantKnowledgeDocument[]>('/assistant/knowledge'),
  knowledgeChunks: (documentId: string) => apiRequest<readonly AssistantKnowledgeChunk[]>(
    `/assistant/knowledge/${documentId}/chunks`),
  uploadKnowledge: (title: string, language: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiRequest<AssistantKnowledgeDocument>(
      `/assistant/knowledge?title=${encodeURIComponent(title)}&language=${encodeURIComponent(language)}`,
      { method: 'POST', body: form });
  },
  publishKnowledge: (documentId: string) => apiRequest<AssistantKnowledgeDocument>(
    `/assistant/knowledge/${documentId}/publish`, { method: 'POST' }),
  approveMemory: (memoryId: string) => apiRequest<AssistantMemory>(`/assistant/memories/${memoryId}/approve`, { method: 'POST' }),
  deleteMemory: (memoryId: string) => apiRequest<void>(`/assistant/memories/${memoryId}`, { method: 'DELETE' }),
  send: (id: string, content: string, locale: string, experienceCode?: string) => apiRequest<AssistantMessage>(
    `/assistant/conversations/${id}/messages`, {
      method: 'POST', body: JSON.stringify({ content, locale, experienceCode }),
    }),
  sendStream: (
    id: string,
    content: string,
    locale: string,
    onEvent?: (event: ApiStreamEvent) => void,
    experienceCode?: string,
  ) => apiEventStream<AssistantMessage>(`/assistant/conversations/${id}/messages/stream`, {
    method: 'POST', body: JSON.stringify({ content, locale, experienceCode }),
  }, onEvent),
  schedules: () => apiRequest<readonly AssistantSchedule[]>('/assistant/schedules'),
  createSchedule: (input: { name: string; prompt: string; intervalMinutes: number }) =>
    apiRequest<AssistantSchedule>('/assistant/schedules', { method: 'POST', body: JSON.stringify(input) }),
  setScheduleState: (schedule: AssistantSchedule, isEnabled: boolean) =>
    apiRequest<AssistantSchedule>(`/assistant/schedules/${schedule.id}/state`, {
      method: 'PUT', body: JSON.stringify({ isEnabled, expectedVersion: schedule.version }),
    }),
  confirmTransaction: (journalId: string) => apiRequest<void>(`/assistant/transactions/${journalId}/confirm`, { method: 'POST' }),
  verifyReceipt: (receiptId: string) => apiRequest<void>(`/assistant/receipts/${receiptId}/verify`, { method: 'POST' }),
  verifyInvoice: (invoiceId: string) => apiRequest<void>(`/assistant/invoices/${invoiceId}/verify`, { method: 'POST' }),
  verifySupplierPayment: (supplierPaymentId: string) => apiRequest<void>(
    `/assistant/supplier-payments/${supplierPaymentId}/verify`, { method: 'POST' }),
  verifyBankTransfer: (bankTransferId: string) => apiRequest<void>(
    `/assistant/bank-transfers/${bankTransferId}/verify`, { method: 'POST' }),
  verifyCashTransaction: (cashTransactionId: string) => apiRequest<void>(
    `/assistant/cash-transactions/${cashTransactionId}/verify`, { method: 'POST' }),
  verifyStatementImport: (statementImportId: string) => apiRequest<void>(
    `/assistant/statement-imports/${statementImportId}/verify`, { method: 'POST' }),
  approveAction: (proposalId: string) => apiRequest<AssistantActionProposal>(`/assistant/actions/${proposalId}/approve`, { method: 'POST' }),
  rejectAction: (proposalId: string, comment: string) => apiRequest<void>(`/assistant/actions/${proposalId}/reject`, {
    method: 'POST', body: JSON.stringify({ comment }),
  }),
  transcribe: (audio: Blob, locale: string) => {
    const form = new FormData();
    form.append('audio', audio, recordedAudioFileName(audio.type));
    return apiRequest<{ text: string }>(`/assistant/audio/transcriptions?locale=${encodeURIComponent(locale)}`, {
      method: 'POST', body: form,
    });
  },
  /**
   * A dictated turn in one round trip. The server transcribes the recording and answers it, so the reply
   * carries both what was heard and what the assistant did about it — the caller never has to hold a
   * transcript that has not been said yet.
   */
  sendVoice: (conversationId: string, audio: Blob, locale: string, audioReply: boolean, experienceCode?: string) => {
    const form = new FormData();
    form.append('audio', audio, recordedAudioFileName(audio.type));
    return apiRequest<AssistantVoiceTurn>(
      `/assistant/conversations/${conversationId}/messages/voice?locale=${encodeURIComponent(locale)}&audioReply=${audioReply}${experienceCode ? `&experienceCode=${encodeURIComponent(experienceCode)}` : ''}`,
      { method: 'POST', body: form });
  },
  speech: (text: string, locale: string) => apiBlob('/assistant/audio/speech', {
    method: 'POST', body: JSON.stringify({ text, locale }),
  }),
};
